import { useEffect, useState, type ReactNode } from 'react'
import { isDesktop } from '../lib/platform.ts'
import { janelaDesktop } from './janela-desktop.ts'

/**
 * Barra do topo do Orbind (SPEC-20261104-0202, ampliada pela SPEC-20261104-1202).
 *
 * Pedido do Cristian: "quero incluir coisas na barra da janela como o Claude
 * faz" e, depois, "essa barra também deve entrar na Web dentro do navegador".
 * Ela existe em toda plataforma (fora do modo compacto): ESQUERDA com o botão
 * do Explorer, sempre presente; CENTRO com o título; DIREITA com as ações
 * pessoais só de contorno — Notificações e Membros — e, no Desktop sem a
 * decoração nativa (≥ 0.3.8), os controles da janela (minimizar, maximizar,
 * fechar) e o arrastar pelo centro. A cor vem dos tokens: segue o tema.
 */
export function BarraDaJanela({
  titulo,
  comControles,
  temExplorer,
  explorerRecolhido,
  aoAlternarExplorer,
  notificacoes,
  membrosAberto,
  aoAlternarMembros,
  workspace,
  escritorio,
  novaJanela,
  sistema,
}: {
  titulo: string
  /** Desktop sem decoração nativa: controles da janela, arrastar e duplo clique. */
  comControles: boolean
  /** A tela atual tem Explorer? Sem ele o botão fica, desabilitado (SPEC-20261104-1202). */
  temExplorer: boolean
  explorerRecolhido: boolean
  aoAlternarExplorer: () => void
  /** Sino na barra, antes de Membros; null quando o plano não tem Notificações. */
  notificacoes: { naoLidas: number; ativa: boolean; aoAbrir: () => void } | null
  /** Pessoas ativas (Membros e status), só contorno (SPEC-20261104-1150). */
  membrosAberto: boolean
  aoAlternarMembros: () => void
  /** Seletor do workspace (nome + seta + menu), ao lado do título (SPEC-20261104-1224). */
  workspace?: ReactNode
  /** Escritório virtual (tabuleiro), primeiro dos controles da direita (SPEC-20261104-1235); null sem o módulo. */
  escritorio?: { ativo: boolean; aoAbrir: () => void } | null
  /** Nova janela do Orbind (só Desktop), vazado, entre Escritório e Notificações (SPEC-20261104-1238). */
  novaJanela?: (() => void) | null
  /** Menu do sistema (Conta, Portal do desenvolvedor, Console), antes do workspace (SPEC-20261104-1444). */
  sistema?: readonly { rotulo: string; icone: string; aoClicar: () => void }[] | null
}) {
  const [maximizada, setMaximizada] = useState(false)

  useEffect(() => {
    if (!comControles) return
    let vivo = true
    const ler = () => void janelaDesktop.estaMaximizada().then((m) => vivo && setMaximizada(m))
    ler()
    // maximizar/restaurar pelo sistema (Win+↑, duplo clique) muda o tamanho
    window.addEventListener('resize', ler)
    return () => {
      vivo = false
      window.removeEventListener('resize', ler)
    }
  }, [comControles])

  return (
    <div className="janela" role="toolbar" aria-label="Barra do Orbind">
      <div className="janela__esq">
        {/* o menu do sistema e o PRIMEIRO da barra (SPEC-20261104-1511) */}
        {sistema && sistema.length > 0 ? <MenuDoSistema itens={sistema} /> : null}
        <button
          type="button"
          className={`janela__btn${explorerRecolhido ? '' : ' janela__btn--on'}`}
          aria-label={!temExplorer ? 'Esta tela não tem Explorer' : explorerRecolhido ? 'Expandir o Explorer' : 'Recolher o Explorer'}
          aria-pressed={temExplorer ? !explorerRecolhido : undefined}
          title={!temExplorer ? 'Esta tela não tem Explorer' : explorerRecolhido ? 'Expandir o Explorer' : 'Recolher o Explorer'}
          disabled={!temExplorer}
          onClick={aoAlternarExplorer}
        >
          <IconeDoExplorer recolhido={explorerRecolhido || !temExplorer} />
        </button>
      </div>
      {/* area livre: no Desktop, segurar arrasta a janela e duplo clique maximiza/restaura */}
      <div
        className="janela__centro"
        onMouseDown={(e) => {
          if (comControles && e.button === 0 && e.detail === 1) void janelaDesktop.arrastar()
        }}
        onDoubleClick={() => {
          if (comControles) void janelaDesktop.alternarMaximizar()
        }}
      >
        {workspace ? (
          // o gatilho e botao, nao area livre: segurar nele nao arrasta a janela
          <span className="janela__ws" onMouseDown={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
            {workspace}
          </span>
        ) : null}
        <span className="janela__titulo">{titulo}</span>
      </div>
      <div className="janela__dir">
        {escritorio ? (
          <button
            type="button"
            className={`janela__btn janela__btn--contorno${escritorio.ativo ? ' janela__btn--on' : ''}`}
            aria-label="Escritório virtual"
            aria-current={escritorio.ativo ? 'page' : undefined}
            title="Escritório virtual"
            onClick={escritorio.aoAbrir}
          >
            <IconeDoEscritorio />
          </button>
        ) : null}
        {novaJanela ? (
          <button
            type="button"
            className="janela__btn janela__btn--contorno"
            aria-label="Abrir nova janela do Orbind"
            title="Nova janela do Orbind"
            onClick={novaJanela}
          >
            <IconeDeNovaJanela />
          </button>
        ) : null}
        {notificacoes ? (
          <button
            type="button"
            className={`janela__btn janela__btn--contorno${notificacoes.ativa ? ' janela__btn--on' : ''}`}
            aria-label={notificacoes.naoLidas > 0 ? `Notificações — ${notificacoes.naoLidas} não lidas` : 'Notificações'}
            aria-expanded={notificacoes.ativa}
            data-notificacoes-toggle
            title="Notificações"
            onClick={notificacoes.aoAbrir}
          >
            <i className="far fa-bell" aria-hidden />
            {notificacoes.naoLidas > 0 ? <span className="janela__badge">{notificacoes.naoLidas}</span> : null}
          </button>
        ) : null}
        {/* Pessoas ativas: só o controle, sem cor sólida — ligado ganha contorno.
            Mesmos aria e data-membros-toggle do botão que vivia no rail. */}
        <button
          type="button"
          className={`janela__btn janela__btn--contorno${membrosAberto ? ' janela__btn--on' : ''}`}
          aria-label={membrosAberto ? 'Fechar painel de membros' : 'Membros e status'}
          aria-expanded={membrosAberto}
          data-membros-toggle
          title="Membros e status"
          onClick={aoAlternarMembros}
        >
          <IconeDePessoas />
        </button>
        {comControles ? (
          <>
            <button type="button" className="janela__ctl" aria-label="Minimizar" title="Minimizar" onClick={() => void janelaDesktop.minimizar()}>
              <svg viewBox="0 0 10 10" aria-hidden>
                <path d="M0 5h10" />
              </svg>
            </button>
            <button
              type="button"
              className="janela__ctl"
              aria-label={maximizada ? 'Restaurar' : 'Maximizar'}
              title={maximizada ? 'Restaurar' : 'Maximizar'}
              onClick={() => void janelaDesktop.alternarMaximizar().then(() => janelaDesktop.estaMaximizada()).then(setMaximizada)}
            >
              {maximizada ? (
                <svg viewBox="0 0 10 10" aria-hidden>
                  <path d="M2.5 2.5V.5h7v7h-2" />
                  <path d="M.5 2.5h7v7h-7z" />
                </svg>
              ) : (
                <svg viewBox="0 0 10 10" aria-hidden>
                  <path d="M.5.5h9v9h-9z" />
                </svg>
              )}
            </button>
            <button type="button" className="janela__ctl janela__ctl--fechar" aria-label="Fechar" title="Fechar" onClick={() => void janelaDesktop.fechar()}>
              <svg viewBox="0 0 10 10" aria-hidden>
                <path d="M0 0l10 10M10 0L0 10" />
              </svg>
            </button>
          </>
        ) : null}
      </div>
    </div>
  )
}

/** O ícone de painel lateral (como o do Claude): a faixa da esquerda cheia = aberto. */
function IconeDoExplorer({ recolhido }: { recolhido: boolean }) {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden>
      <rect x="1.5" y="2.5" width="13" height="11" rx="2" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M6 2.5v11" stroke="currentColor" strokeWidth="1.4" />
      {recolhido ? null : <rect x="1.5" y="2.5" width="4.5" height="11" rx="2" fill="currentColor" opacity="0.55" />}
    </svg>
  )
}

/**
 * Pessoas ativas VAZADAS (SPEC-20261104-1202-icone-de-pessoas-vazado, pedido do
 * Cristian: "o ícone eu quero vazado, com o fundo transparente, e somente as
 * bordas dos bonequinhos"): dois contornos, sem preenchimento — o Font Awesome
 * grátis só tem a versão cheia deste ícone.
 */
function IconeDePessoas() {
  return (
    <svg viewBox="0 0 20 16" width="18" height="14" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="5" r="3" />
      <path d="M1.5 14.5c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
      <circle cx="14" cy="5.5" r="2.4" />
      <path d="M13.5 9.5c2.7 0 5 1.9 5 5" />
    </svg>
  )
}

/**
 * Planta do escritório em tabuleiro (SPEC-20261104-1235, desenho pedido pelo
 * Cristian): quadrado arredondado dividido em salas, com pontos de presença.
 * Vazado, no mesmo peso de linha dos outros controles da barra.
 */
function IconeDoEscritorio() {
  return (
    <svg viewBox="0 0 18 18" width="16" height="16" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
      <rect x="1.5" y="1.5" width="15" height="15" rx="3" />
      <path d="M1.5 8.5h15M8.5 8.5v8" />
      <circle cx="5.5" cy="5" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="11.5" cy="5" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="5" cy="12.5" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  )
}

/**
 * Menu do sistema na barra (SPEC-20261104-1444, pedido do Cristian): o ícone
 * de três linhas (a do meio mais curta) antes do workspace abre Conta, Portal
 * do desenvolvedor e Console — itens que saíram do menu do perfil.
 */
function MenuDoSistema({ itens }: { itens: readonly { rotulo: string; icone: string; aoClicar: () => void }[] }) {
  const [aberto, setAberto] = useState(false)
  return (
    <div className="wsbar janela__sistema">
      <button
        type="button"
        className="janela__btn"
        aria-label="Menu do sistema"
        aria-expanded={aberto}
        title="Conta, Portal do desenvolvedor e Console"
        onClick={() => setAberto((v) => !v)}
      >
        <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <path d="M2.5 4.5h11M2.5 8h7M2.5 11.5h11" />
        </svg>
      </button>
      {aberto ? (
        <>
          <div className="shell__popover-scrim" onClick={() => setAberto(false)} aria-hidden />
          <div className="wsbar__menu" role="menu" aria-label="Menu do sistema">
            {itens.map((item) => (
              <button
                key={item.rotulo}
                type="button"
                role="menuitem"
                className="shell__popover-item"
                onClick={() => {
                  setAberto(false)
                  item.aoClicar()
                }}
              >
                <i className={item.icone} aria-hidden />
                {item.rotulo}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  )
}

/** Duas janelas sobrepostas, vazadas (SPEC-20261104-1238): abrir outra janela do Orbind. */
function IconeDeNovaJanela() {
  return (
    <svg viewBox="0 0 18 18" width="16" height="16" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
      <rect x="1.5" y="5.5" width="11" height="11" rx="2" />
      <path d="M1.5 8.5h11" />
      <path d="M5.5 5.5v-2a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-2" />
    </svg>
  )
}

/**
 * Onde a barra entra e com o quê: `mostrar` em toda plataforma (o AppShell
 * tira no modo compacto); `comControles` só no Desktop e só quando a janela
 * veio sem a decoração nativa (builds novos) — em builds antigos, com a barra
 * nativa, ficariam dois jogos de controles.
 */
export function useBarraDaJanela(): { mostrar: boolean; comControles: boolean } {
  const [comControles, setComControles] = useState(false)
  useEffect(() => {
    if (!isDesktop()) return
    let vivo = true
    void janelaDesktop.semDecoracao().then((sem) => vivo && setComControles(sem))
    return () => {
      vivo = false
    }
  }, [])
  return { mostrar: true, comControles }
}
